import * as React from 'react';
import { ComponentProps, ReactElement, useMemo } from 'react';

import { useAtomValue } from 'jotai';

import { translate } from '~l10n/client';
import { languageAtom } from '~shared/atoms';
import { formatBytes } from '~teacher/components/FileDrop/utilities/formatBytes';

import { FileDropContainer } from './FileDropContainer';
import { FileHandlerConfig } from './FileHandlers/FileHandlerConfig';
import { FileDropCallbacks, FileTask } from './hooks/useFileDropConfig';

interface FileDropProps extends FileHandlerConfig, FileDropCallbacks {
  /** The label for the file drop */
  label: React.ReactElement<ComponentProps<typeof FileDrop.Label>>;
  helperText?: ReactElement<ComponentProps<typeof FileDrop.HelperText>>;
  /** The maximum number of files that can be uploaded */
  maxFileCount?: number;
  /** The maximum file size in bytes */
  maxFileSize: number;
  /** Whether the file drop is disabled */
  isDisabled?: boolean;
  /** The files that are currently being uploaded */
  fileTasks: Array<FileTask>;
}

export function FileDrop({
  isDisabled = false,
  maxFileSize,
  commonExtensions,
  maxFileCount = 1,
  label,
  helperText,
  fileTasks,
  addFileTasks,
  removeFileTasks,
  retryFileTasks,
}: FileDropProps) {
  const language = useAtomValue(languageAtom);

  const acceptedFileTypes = Array.from(commonExtensions).map(
    (ext) => `.${ext}`
  );

  const hasUploadedMaxFileCount = maxFileCount === fileTasks.length;
  const hasSingleFileError =
    maxFileCount === 1 && fileTasks[0]?.status === 'error';

  const disableControl = useMemo(() => {
    if (isDisabled) {
      return true;
    }

    // If this control only allows a single file, and it had an error
    // do not block uploads
    if (hasSingleFileError) {
      return false;
    }
    return hasUploadedMaxFileCount;
  }, [isDisabled, hasUploadedMaxFileCount, hasSingleFileError]);

  return (
    <FileDropContainer
      acceptedFileTypes={acceptedFileTypes}
      addFileTasks={addFileTasks}
      removeFileTasks={removeFileTasks}
      retryFileTasks={retryFileTasks}
      currentFilesCount={fileTasks.length}
      maxFileCount={maxFileCount}
      label={label}
      files={fileTasks}
      isDisabled={disableControl}
      fileDropButtonText={
        <FileDropContainer.ButtonText>
          {maxFileCount > 1
            ? translate('Drag files here or click to upload')
            : translate('Drag a file here or click to upload')}
        </FileDropContainer.ButtonText>
      }
      fileDropButtonHelperText={
        <FileDropContainer.ButtonHelperText>
          {maxFileCount > 1 ? (
            <>
              {translate('Max {0}').format(
                formatBytes({ bytes: maxFileSize, locale: language })
              )}{' '}
              • {acceptedFileTypes.join(' ')}
            </>
          ) : fileTasks.length === 0 || hasSingleFileError ? (
            <>
              {translate('Max {0}').format(
                formatBytes({ bytes: maxFileSize, locale: language })
              )}{' '}
              • {acceptedFileTypes.join(' ')}
            </>
          ) : (
            <>{translate('Remove the file to upload another.')}</>
          )}
        </FileDropContainer.ButtonHelperText>
      }
      helperText={helperText}
    />
  );
}

FileDrop.Label = FileDropContainer.Label;
FileDrop.HelperText = FileDropContainer.HelperText;
